import axios from "axios";
import Cookies from "js-cookie";
import { getSecurityMe } from "./CallCenterSecurity";
import { mapSecurityMeToPermissions } from "../utils/ccPermissions";
import { notifyPermissionsChanged, PERMISSIONS_STORAGE_KEY as PERMS_KEY } from "../utils/permissions";
import { isPlatformSuperAdmin } from "../utils/organizationContext";
import { getAppTypeHeader, isDesktopApp } from "../utils/isDesktop";
import { getCallCenterBaseUrl } from "./CallCenterApi";

const BASE_URL = () => getCallCenterBaseUrl();
const PERMISSIONS_STORAGE_KEY = PERMS_KEY;

/**
 * Maps login API body.value to the shape used across the app.
 * Supports both legacy ({ userDetails, accessToken }) and call-center ({ token, role, ... }) payloads.
 */
export const mapLoginResponseToUser = (value) => {
  if (!value) return null;

  if (value.userDetails) {
    const legacy = value.userDetails;
    const accessToken = value.accessToken || value.token;
    return {
      ...legacy,
      accessToken,
    };
  }

  const accessToken = value.token || value.accessToken;
  const role = value.role || "";
  const userType = String(value.userType || role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  return {
    userId: value.userId,
    email: value.email,
    name: value.name,
    role,
    userType,
    accessToken,
    organizationId: value.organizationId ?? null,
    organizationName: value.organizationName ?? null,
    organizationCode: value.organizationCode ?? null,
    organization: value.organizationId
      ? {
          organizationId: value.organizationId,
          organizationName: value.organizationName,
          organizationCode: value.organizationCode,
        }
      : value.organization,
    profilePic: value.profilePic ?? null,
    userVisibility: value.userVisibility,
    firstTimeLogin: value.firstTimeLogin ?? "false",
    departmentList: value.departmentList,
    projectList: value.projectList,
    canAddEditUser: value.canAddEditUser ?? userType === "super_admin",
    canCreateDashboard: value.canCreateDashboard,
    canAccessCustomerSupport: value.canAccessCustomerSupport,
    allowedPages: value.allowedPages,
  };
};
  // const response = await axios.post(`${BASE_URL()}api/auth/login`, values, {

export const loginUser = async (values) => {
  try {
    const response = await axios.post(`${BASE_URL()}api/auth/login`, values, {
      headers: {
        "X-App-Type": getAppTypeHeader(),
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    const envelope = error?.response?.data;
    if (envelope?.header?.code != null) {
      return envelope;
    }
    throw error;
  }
};

export const getUserPermissions = async (accessToken) => {
  const response = await axios.get(`${BASE_URL()}api/v1/user/permissions`, {
    headers: {
      "X-App-Type": getAppTypeHeader(),
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

/**
 * Loads permissions from call-center backend (`/api/security/me`) and persists them
 * for `hasScreenAccess`. Falls back to legacy BlueField permissions when available.
 *
 * @param {string} accessToken
 * @param {{ expires?: Date }} [options]
 * @returns {Promise<{ permissions: Array, fullAccess: boolean }|null>}
 */
export const fetchAndStoreUserPermissions = async (accessToken, options = {}) => {
  const { expires } = options;

  const persist = (value) => {
    if (!value) return null;
    const serialized = JSON.stringify(value);
    if (expires) {
      Cookies.set(PERMISSIONS_STORAGE_KEY, serialized, { expires });
    }
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(PERMISSIONS_STORAGE_KEY, serialized);
    }
    notifyPermissionsChanged();
    return value;
  };

  try {
    const securityMe = await getSecurityMe();
    if (securityMe?.organizationId && typeof localStorage !== "undefined" && !isPlatformSuperAdmin()) {
      localStorage.setItem("selectedOrgId", String(securityMe.organizationId));
      localStorage.setItem("selectedOrgName", securityMe.organizationName || "");
    }
    let mapped = mapSecurityMeToPermissions(securityMe);
    if (isDesktopApp() && isPlatformSuperAdmin()) {
      mapped = { ...mapped, fullAccess: true };
    }
    return persist(mapped);
  } catch (callCenterError) {
    console.warn("Call-center security/me failed, trying legacy permissions:", callCenterError);
  }

  try {
    const permissionsResponse = await getUserPermissions(accessToken);
    if (permissionsResponse?.header?.code === 600 && permissionsResponse?.body?.value) {
      return persist(permissionsResponse.body.value);
    }
  } catch (legacyError) {
    console.warn("Legacy user permissions fetch failed:", legacyError);
  }

  return null;
};
export const storeFcmToken = async (token, accessToken) => {
  const response = await axios.post(
    `${BASE_URL()}api/v1/notification/store-fcm-token`,
    { token },
    {
      headers: {
        "X-App-Type": getAppTypeHeader(),
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return response.data;
};

export const forgotPassword = async (email) => {
  const response = await axios.post(
    `${BASE_URL()}api/v1/user/forgot-password`,
    {},
    {
      params: { email },
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const checkOtp = async (otp, email) => {
  const response = await axios.post(
    `${BASE_URL()}api/v1/user/check-otp`,
    { otp, email },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const resetPassword = async (email, password) => {
  const response = await axios.post(
    `${BASE_URL()}api/v1/user/new-password`,
    { email, password },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};
